import axios from "axios";
import { getValidToken } from "./auth";
import { GENERIC_ERROR_MESSAGE, resolveErrorMessage } from "../constants/errorMessages";

export class ApiRequestError extends Error {
  constructor({ message, code, fieldErrors, details, status } = {}) {
    super(message || GENERIC_ERROR_MESSAGE);
    this.name = "ApiRequestError";
    this.code = code || "UNKNOWN_ERROR";
    this.fieldErrors = fieldErrors || {};
    this.details = details;
    this.status = status;
  }
}

const normalizeFieldErrors = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((acc, [field, message]) => {
    if (typeof message === "string" && message.trim().length > 0) {
      acc[field] = message;
    }
    return acc;
  }, {});
};

const normalizeApiError = (error) => {
  if (!error) {
    return new ApiRequestError({
      message: GENERIC_ERROR_MESSAGE,
    });
  }

  if (error instanceof ApiRequestError) {
    return error;
  }

  if (axios.isCancel(error) || error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
    return new ApiRequestError({
      code: "ERR_CANCELED",
      message: "Request canceled.",
    });
  }

  const responseData = error.response?.data;
  const structuredError =
    responseData?.errorDetails ||
    (responseData?.error && typeof responseData.error === "object"
      ? responseData.error
      : null);

  // Axios puts its own transport text ("Network Error", "timeout of 0ms
  // exceeded") on error.message, so only trust it when a response came back.
  const hasResponse = Boolean(error.response);
  const message = resolveErrorMessage({
    message:
      structuredError?.message ||
      (typeof responseData?.message === "string" ? responseData.message : "") ||
      (typeof responseData?.error === "string" ? responseData.error : ""),
    code: structuredError?.code || responseData?.code,
    hasResponse,
  });

  const code =
    structuredError?.code ||
    responseData?.code ||
    responseData?.errorCode ||
    "UNKNOWN_ERROR";

  const fieldErrors = normalizeFieldErrors(
    structuredError?.fieldErrors || responseData?.fieldErrors,
  );

  return new ApiRequestError({
    message,
    code,
    fieldErrors,
    details: structuredError?.details || responseData?.details,
    status: error.response?.status,
  });
};

export const authorizedRequest = async ({
  url,
  method = "get",
  data,
  params,
  headers = {},
  signal,
}) => {
  const auth = getValidToken();

  if (!auth?.token) {
    throw new ApiRequestError({
      code: "AUTH_REQUIRED",
      message: "Your session has expired. Please log in again.",
      fieldErrors: {},
      status: 401,
    });
  }

  try {
    const response = await axios({
      method,
      url,
      data,
      params,
      signal,
      headers: {
        Authorization: `Bearer ${auth.token}`,
        ...headers,
      },
    });

    return response.data;
  } catch (err) {
    throw normalizeApiError(err);
  }
};

export default authorizedRequest;
